<template>
  <gs-drawer
    :value="open"
    class="gs-add-video-drawer"
    size="wide"
    close-label="Cancel"
    close-variant="ghost"
    title="Add a video"
    subtitle="Start with your video link and title. Customise the player whenever you’re ready."
    @close="handleClose()"
  >
    <div class="gs-add-video">
      <div class="gs-add-video__segment" role="tablist" aria-label="Add a video source">
        <button
          v-for="tab in sourceTabs"
          :key="tab.id"
          type="button"
          role="tab"
          :data-tab="tab.id"
          :aria-selected="activeTab === tab.id ? 'true' : 'false'"
          :class="{ 'is-active': activeTab === tab.id }"
          :disabled="!!pendingId && activeTab !== tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <gs-card v-if="remainingVideos <= 0" class="gs-add-video__limit">
        <p>{{ $t('frontend_groovevideo_videos_index_to_unlock_more_videos') }}</p>
        <gs-button variant="outline" @click="$emit('upgrade')">See plans</gs-button>
      </gs-card>

      <div v-else class="gs-add-video__body">
        <mux-connect-callout
          v-if="activeTab !== 'youtube' && !muxConnected"
          :status="muxStatus"
          :checking="muxChecking"
          :error="muxStatusError"
          @connect="connectMux()"
          @check="refreshMuxStatus()"
        />

        <div v-else class="gs-add-video__form">
          <template v-if="activeTab === 'youtube'">
            <gs-field
              label="YouTube URL"
              hint="Use the link from YouTube's Share button. Unlisted videos work."
              :error="urlError"
            >
              <input
                data-field="url"
                type="url"
                inputmode="url"
                placeholder="https://youtu.be/…"
                class="gs-add-video__input"
                :class="{ 'gs-add-video__input--error': urlError }"
                v-model="url" :disabled="!!pendingId"
              />
            </gs-field>

            <div v-if="videoId" class="gs-add-video__thumb">
              <img data-thumb :src="thumbCandidates[thumbIndex]" alt="" @error="thumbIndex++" />
            </div>
          </template>

          <mux-upload-panel
            v-else-if="activeTab === 'upload'"
            ref="muxUpload"
            :disabled="!!pendingId || saving"
            @file-chosen="onMuxFileChosen"
            @cleared="clearMuxFile()"
            @hook:beforeDestroy="clearMuxFile()"
          />

          <mux-asset-picker
            v-else
            :value="muxAsset && muxAsset.asset_id"
            :load-page="searchAssets"
            :browse-page="browseAssets"
            @input="onMuxAssetChosen"
            @needs-connect="refreshMuxStatus()"
          />

          <video-format-picker :value="appearanceModel.video_dimensions.aspect_ratio" @input="setFormat" />

          <gs-field label="Title">
            <input data-field="title" type="text" required class="gs-add-video__input" v-model="title" :disabled="!!pendingId" />
          </gs-field>

          <gs-field label="Category">
            <treeselect
              v-model="categoryId"
              :multiple="false"
              :clearable="false"
              :options="categories"
              :normalizer="normalizer"
            />
            <button type="button" class="gs-add-video__new-category" @click="$emit('new-category')">
              + New category
            </button>
          </gs-field>

          <details class="gs-add-video__section">
            <summary><span>Description</span><span class="gs-add-video__section-note">Optional</span></summary>
            <div class="gs-add-video__section-content">
            <gs-field label="Description">
            <textarea
              data-field="description"
              rows="3"
              class="gs-add-video__input"
              v-model="description"
            ></textarea>
            </gs-field>
            </div>
          </details>

          <details class="gs-add-video__section gs-add-video__appearance">
            <summary><span>Player theme &amp; colours</span><span class="gs-add-video__section-note">Optional</span></summary>
            <div class="gs-add-video__section-content">
              <theme-section :model="appearanceModel" />
              <div class="gs-add-video__preview">
                <h3>Player preview</h3>
                <editor-canvas-preview :model="appearanceModel" />
                <p class="gs-add-video__appearance-hint">YouTube, Vimeo and Wistia are visual themes. Your video source stays the same.</p>
              </div>
            </div>
          </details>
          <p v-if="uploadFailed && activeTab === 'upload'" role="status">The upload did not finish, so its draft was removed. Choose Upload video to try again with a fresh upload.</p>
          <p v-else-if="pendingId" role="status">Your draft was created. Retry to finish its settings; another video will not be created.</p>
          <div class="gs-switch-row">
            <span class="gs-switch-row__text">
              <span class="gs-switch-row__label">Publish immediately</span>
              <span class="gs-switch-row__help">Turn this off to keep the video as a draft.</span>
              <span v-if="publishesLeft <= 0" class="gs-switch-row__hint">
                You have reached your publishing limit
              </span>
            </span>
            <span
              class="gs-add-video__toggle-wrap"
              :class="{ 'is-disabled': publishesLeft <= 0 }"
              :aria-disabled="publishesLeft <= 0 ? 'true' : null"
            >
              <gd-toggle v-model="publishNow" :disabled="publishesLeft <= 0" @change="publishTouched = true" />
            </span>
          </div>
        </div>

        <details v-if="activeTab === 'youtube'" class="gs-add-video__section gs-add-video__why">
          <summary><span>Why a YouTube link?</span></summary>
          <div class="gs-add-video__section-content">
            <ul class="gs-add-video__why-list">
              <li>
                <strong>Invisible source.</strong> Viewers see your player, your colours and your calls to
                action. Never a YouTube page, logo, ad, card or related video.
              </li>
              <li>
                <strong>Keep it unlisted.</strong> Upload to YouTube as Unlisted and the video stays out of
                YouTube search and off your channel page. It lives inside your GrooveVideo player.
              </li>
              <li>
                <strong>Fast everywhere.</strong> YouTube encodes in minutes and serves from servers close to
                every viewer, with unlimited storage at no cost to you.
              </li>
              <li>
                <strong>One link, everywhere.</strong> Publish once and drop the same player into funnels,
                courses, blog posts and emails.
              </li>
            </ul>
          </div>
        </details>
      </div>

      <div v-if="remainingVideos > 0" class="gs-add-video__footer">
        <gs-button
          variant="primary"
          data-action="add"
          :disabled="!canSubmit || saving"
          @click="submit()"
        >
          {{ submitLabel }}
        </gs-button>
      </div>
    </div>
  </gs-drawer>
</template>

<script>
import { applyPlayerDefaults } from '@/pages/videos/editor/player/playerOptions';
import VideoFormatPicker from './VideoFormatPicker.vue';
import { setVideoFormat } from '@/player/videoFormat';
import { initializeModernVideo, syncModernAppearance } from '@/pages/videos/editor/player/newAppearance';
import ThemeSection from '@/pages/videos/editor/player/ThemeSection.vue';
import EditorCanvasPreview from '@/pages/videos/editor/EditorCanvasPreview.vue';
import Notify from '@groovepages/gd-lib/notify';
import axios from 'axios';
// The treeselect stylesheet is already imported by LibraryHeader.vue and
// pages/videos/index.vue, the only places this drawer is mounted; importing
// it again here would pull the real .css file into Jest (jest-transform-stub
// only covers files outside node_modules) and fail the component's own spec.
import Treeselect from '@riophae/vue-treeselect';
import Video from '@/models/Video';
import SourceType from '@/models/SourceType';
import MuxConnectCallout from '@/components/library/mux/MuxConnectCallout.vue';
import MuxUploadPanel from '@/components/library/mux/MuxUploadPanel.vue';
import MuxAssetPicker from '@/components/library/mux/MuxAssetPicker.vue';
import { getMuxStatus, createMuxUpload, importMuxAsset, searchMuxAssets, listMuxAssets, deleteVideo, muxError } from '@/services/muxApi';
import { openMuxConnect, isTrustedConnectUrl } from '@/utils/muxOpener';
import { parseYouTubeId, isYouTubeUrl, youtubeThumbnailCandidates, fetchYouTubeTitle } from '@/utils/youtube';

// Category id 1 is the account's default category; the API does not guarantee
// it comes back first, so preselect it explicitly the way the Player panel
// hoists it. Falls back to whatever the API did return first.
export function defaultCategoryId(categories) {
  if (!categories || !categories.length) return 1;
  const hasDefault = categories.some(category => category && category.id === 1);
  return hasDefault ? 1 : categories[0].id;
}

// Same shape as muxUpload's cancelled error: submit() ends quietly on it.
function closedError() {
  const error = new Error('The drawer was closed');
  error.cancelled = true;
  return error;
}

export const SOURCE_TABS = [
  { id: 'youtube', label: 'YouTube link' },
  { id: 'upload', label: 'Upload to Mux' },
  { id: 'import', label: 'Import from Mux' },
];

export default {
  name: 'AddVideoDrawer',
  components: { VideoFormatPicker, Treeselect, ThemeSection, EditorCanvasPreview, MuxConnectCallout, MuxUploadPanel, MuxAssetPicker },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    remainingVideos: {
      type: Number,
      default: Infinity,
    },
    publishesLeft: {
      type: Number,
      default: 0,
    },
    createVideo: {
      type: Function,
      default: payload => new Video(payload).save(),
    },
    attachSource: {
      type: Function,
      default: payload => new SourceType(payload).save(),
    },
    configureVideo: {
      type: Function,
      default: async (id, appearanceModel) => {
        const model = await Video.$find(id);
        applyPlayerDefaults(model);
        initializeModernVideo(model);
        setVideoFormat(model, appearanceModel.video_dimensions.aspect_ratio);
        model.settings.appearance = { ...appearanceModel.settings.appearance };
        model.settings.color = { ...appearanceModel.settings.color };
        syncModernAppearance(model);
        return axios.post('/video_details', model);
      },
    },
    publish: {
      type: Function,
      default: (id, value) => axios.post(`videos/${id}/publish`, { is_published: value }),
    },
    titleLookup: {
      type: Function,
      default: fetchYouTubeTitle,
    },
    fetchMuxStatus: { type: Function, default: getMuxStatus },
    createUpload: { type: Function, default: createMuxUpload },
    importAsset: { type: Function, default: importMuxAsset },
    // (q, cursor) => server-side search of the whole Mux library.
    searchAssets: { type: Function, default: searchMuxAssets },
    // (cursor) => plain paged listing; the picker falls back to it if search is down.
    browseAssets: { type: Function, default: listMuxAssets },
    removeVideo: { type: Function, default: id => deleteVideo(id) },
    openConnect: { type: Function, default: openMuxConnect },
  },
  data() {
    const appearanceModel = {};
    applyPlayerDefaults(appearanceModel);
    initializeModernVideo(appearanceModel);
    return {
      appearanceModel,
      sourceTabs: SOURCE_TABS,
      pendingId: null,
      sourceAttached: false,
      appearanceSaved: false,
      activeTab: 'youtube',
      url: '',
      title: '',
      categoryId: defaultCategoryId(this.categories),
      description: '',
      publishNow: this.publishesLeft > 0,
      publishTouched: false,
      saving: false,
      thumbIndex: 0,
      muxStatus: null,
      muxChecking: false,
      muxStatusError: '',
      muxFile: null,
      muxFileTitle: '',
      uploadFailed: false,
      closing: false,
      muxAsset: null,
      pendingUploadUrl: '',
      uploadDone: false,
      uploading: false,
      connectSession: null,
      // Bumped on every close; an upload submit started before a close must not carry on after it.
      closeCount: 0,
    };
  },
  computed: {
    videoId() {
      return parseYouTubeId(this.url);
    },
    urlError() {
      return this.url && !isYouTubeUrl(this.url) ? "That doesn't look like a YouTube link." : '';
    },
    thumbCandidates() {
      return this.videoId ? youtubeThumbnailCandidates(this.videoId) : [];
    },
    muxConnected() {
      return Boolean(this.muxStatus && this.muxStatus.connected);
    },
    canSubmit() {
      const hasTitle = Boolean(this.title && this.title.trim());
      if (this.activeTab === 'upload') return this.muxConnected && Boolean(this.muxFile) && hasTitle;
      if (this.activeTab === 'import') return this.muxConnected && Boolean(this.muxAsset) && hasTitle;
      return Boolean(this.videoId) && hasTitle;
    },
    submitLabel() {
      if (this.activeTab === 'upload') {
        if (this.uploading) return 'Uploading…';
        return this.saving ? 'Starting upload…' : 'Upload video';
      }
      if (this.activeTab === 'import') return this.saving ? 'Importing…' : 'Import video';
      return this.saving ? 'Adding…' : 'Add video';
    },
  },
  watch: {
    videoId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.thumbIndex = 0;
        if (!this.title) {
          this.titleLookup(this.url).then(fetched => {
            if (fetched && !this.title) this.title = fetched;
          });
        }
      }
    },
    open(isOpen) {
      if (isOpen) this.resetForm();
      else this.stopConnectSession();
      this.closing = false;
    },
    publishesLeft(newValue) {
      if (!this.publishTouched) {
        this.publishNow = newValue > 0;
      }
    },
    activeTab(tab) {
      if (tab !== 'youtube' && !this.muxStatus && !this.muxChecking) this.refreshMuxStatus();
    },
  },
  beforeDestroy() {
    this.stopConnectSession();
  },
  methods: {
    setFormat(ratio) { setVideoFormat(this.appearanceModel, ratio); },
    normalizer(node) {
      return {
        id: node.id,
        label: node.category,
        children: node.child && node.child.length ? node.child : undefined,
      };
    },
    selectCategory(id) {
      this.categoryId = id;
    },
    resetForm() {
      this.pendingId = null;
      this.sourceAttached = false;
      this.appearanceSaved = false;
      const appearanceModel = {};
      applyPlayerDefaults(appearanceModel);
      this.appearanceModel = initializeModernVideo(appearanceModel);
      this.activeTab = 'youtube';
      this.url = '';
      this.title = '';
      this.description = '';
      this.thumbIndex = 0;
      this.categoryId = defaultCategoryId(this.categories);
      this.publishNow = this.publishesLeft > 0;
      this.publishTouched = false;
      this.stopConnectSession();
      this.muxStatus = null;
      this.muxStatusError = '';
      this.muxFile = null;
      this.muxFileTitle = '';
      this.uploadFailed = false;
      this.closing = false;
      this.muxAsset = null;
      this.pendingUploadUrl = '';
      this.uploadDone = false;
      this.uploading = false;
    },
    async refreshMuxStatus() {
      this.muxChecking = true;
      this.muxStatusError = '';
      try {
        this.muxStatus = await this.fetchMuxStatus();
      } catch (e) {
        const info = muxError(e, 'We could not check your Mux connection. Try again.');
        this.muxStatus = { connected: false, connect_url: info.connectUrl };
        this.muxStatusError = info.message;
      } finally {
        this.muxChecking = false;
      }
    },
    connectMux() {
      const url = this.muxStatus && this.muxStatus.connect_url;
      if (!isTrustedConnectUrl(url)) {
        this.muxStatusError = 'Mux cannot be connected from here right now. Try again later.';
        return;
      }
      this.stopConnectSession();
      // The popup's message is only a hint; the connection is always re-read from GrooveAPI.
      this.connectSession = this.openConnect(url, () =>
        this.refreshMuxStatus().then(() => {
          if (this.muxConnected) this.stopConnectSession();
        })
      );
    },
    stopConnectSession() {
      if (this.connectSession) this.connectSession.stop();
      this.connectSession = null;
    },
    onMuxFileChosen(file) {
      this.muxFile = file;
      // Suggest the file name, replacing the previous file's suggestion but never a typed title.
      if (!this.title || (this.muxFileTitle && this.title === this.muxFileTitle)) {
        this.title = file.name.replace(/\.[^.]+$/, '');
        this.muxFileTitle = this.title;
      }
    },
    // The panel was cleared or unmounted (tab switch, connect callout): its file is gone, so forget it
    // and the title it suggested, or "Upload video" would create a row with nothing to upload.
    clearMuxFile() {
      if (this.muxFileTitle && this.title === this.muxFileTitle) this.title = '';
      this.muxFile = null;
      this.muxFileTitle = '';
      this.uploadFailed = false;
    },
    onMuxAssetChosen(asset) {
      this.muxAsset = asset;
      if (!this.title && asset.title) this.title = asset.title;
    },
    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      try {
        if (this.activeTab === 'upload') await this.submitUpload();
        else if (this.activeTab === 'import') await this.submitImport();
        else await this.submitYoutube();
      } catch (e) {
        if (e && e.cancelled) return;
        if (this.activeTab === 'youtube') {
          Notify.fail((e && e.response && e.response.data && e.response.data.message) || 'We could not add the video. Please try again.');
          return;
        }
        // Only upload errors carry copy written for people; anything else (a TypeError, say) gets the friendly fallback.
        const fallback = (e && e.fromUpload && e.message) || 'We could not add the video. Please try again.';
        const info = muxError(e, fallback);
        if (info.needsConnect) this.refreshMuxStatus();
        Notify.fail(info.message);
      } finally {
        this.saving = false;
        this.uploading = false;
      }
    },
    async submitYoutube() {
      const source = `https://www.youtube.com/watch?v=${this.videoId}`;
      if (!this.pendingId) {
        const saved = await this.createVideo({
          name: this.title,
          category: this.categoryId,
          description: this.description,
          video_source_type: 2,
          video_source: source,
        });
        this.pendingId = saved.data ? saved.data.id : saved.id;
      }
      const id = this.pendingId;
      if (!this.sourceAttached) {
        await this.attachSource({ vid: id, type: 2, source, name: this.title, category: this.categoryId });
        this.sourceAttached = true;
      }
      await this.saveAppearance(id);
      await this.finish(id, 'Video added');
    },
    async submitUpload() {
      const closeCount = this.closeCount;
      const closedSince = () => this.closeCount !== closeCount;
      if (!this.pendingId) {
        const created = await this.createUpload({ name: this.title.trim(), category: this.categoryId, description: this.description });
        if (closedSince()) {
          // The drawer closed while the row was being created: remove it rather than upload into a hidden drawer.
          await this.discardVideo(created.video.id);
          throw closedError();
        }
        this.pendingId = created.video.id;
        this.pendingUploadUrl = created.upload_url;
      }
      const id = this.pendingId;
      await this.saveAppearance(id);
      if (closedSince()) throw closedError();
      if (!this.uploadDone) {
        this.uploading = true;
        this.uploadFailed = false;
        const upload = this.$refs.muxUpload.start(this.pendingUploadUrl);
        // Closing aborts the upload and rejects this promise; never let that surface as an unhandled rejection.
        upload.catch(() => {});
        try {
          await upload;
        } catch (e) {
          if ((e && e.cancelled) || closedSince()) throw e;
          // Mux direct-upload URLs are single-use and expire, so a retry needs a fresh upload (and row).
          // Keep the chosen file; drop the row this upload belonged to.
          const failedId = this.pendingId;
          this.pendingId = null;
          this.pendingUploadUrl = '';
          this.appearanceSaved = false;
          this.uploadFailed = true;
          this.discardVideo(failedId);
          const error = e instanceof Error ? e : new Error('The upload failed. Try again.');
          error.fromUpload = true;
          throw error;
        }
        this.uploadDone = true;
      }
      await this.finish(id, 'Upload finished. Mux is preparing your video.');
    },
    async submitImport() {
      if (!this.pendingId) {
        const video = await this.importAsset({
          asset_id: this.muxAsset.asset_id,
          name: this.title.trim(),
          category: this.categoryId,
          description: this.description,
        });
        this.pendingId = video.id;
      }
      await this.saveAppearance(this.pendingId);
      await this.finish(this.pendingId, 'Video imported from Mux');
    },
    async saveAppearance(id) {
      if (this.appearanceSaved) return;
      await this.configureVideo(id, this.appearanceModel);
      this.appearanceSaved = true;
    },
    async finish(id, message) {
      if (this.publishNow && this.publishesLeft > 0) await this.publish(id, true);
      Notify.success(message);
      this.$emit('created', id);
      this.resetForm();
    },
    async discardVideo(id) {
      try {
        await this.removeVideo(id);
      } catch (e) {
        // The unfinished row stays in the library as "Upload not finished" and can be deleted there.
      }
    },
    handleClose() {
      if (this.closing) return;
      this.closing = true;
      this.closeCount += 1;
      this.stopConnectSession();
      let abandonedId = null;
      // Leaving mid-upload abandons it; remove the row so no empty Mux video is left behind.
      if (this.activeTab === 'upload' && this.pendingId && !this.uploadDone) {
        abandonedId = this.pendingId;
        this.pendingId = null;
        if (this.$refs.muxUpload) this.$refs.muxUpload.abort();
      }
      this.$emit('close');
      // Delete in the background so closing never waits on the network.
      if (abandonedId) this.discardVideo(abandonedId);
    },
  },
};
</script>

<style scoped>
/* Keep creation controls full-width; a second column cramped theme previews. */
.gs-add-video-drawer ::v-deep .gs-drawer__panel { width: min(100%, 800px); }
.gs-add-video__section { border: 1px solid var(--gs-border); border-radius: var(--gs-radius-md); background: var(--gs-surface); }
.gs-add-video__section summary { display: flex; align-items: center; gap: 12px; min-height: 60px; padding: 16px 20px; cursor: pointer; font-size: 14px; font-weight: 600; list-style: none; }
.gs-add-video__section summary::-webkit-details-marker { display: none; }
.gs-add-video__section summary::after { content: ''; flex: none; width: 8px; height: 8px; margin-left: auto; border-right: 1.5px solid var(--gs-muted-foreground); border-bottom: 1.5px solid var(--gs-muted-foreground); transform: rotate(45deg) translateY(-2px); }
.gs-add-video__section[open] > summary::after { transform: rotate(225deg) translateY(-2px); }
.gs-add-video__section summary:focus-visible { outline: 2px solid var(--gs-active); outline-offset: -3px; border-radius: var(--gs-radius-md); }
.gs-add-video__section-note { font-size: 12px; font-weight: 400; color: var(--gs-muted-foreground); }
.gs-add-video__section-content { padding: 0 20px 20px; }
.gs-add-video__section[open] > summary { border-bottom: 1px solid var(--gs-border); margin-bottom: 20px; }
.gs-add-video__appearance ::v-deep .gs-player-section { padding-top: 0; }
.gs-add-video__appearance ::v-deep .gs-player-section__colours { grid-template-columns: minmax(0, 1fr); }
.gs-add-video__preview { margin-top: 24px; padding-top: 24px; border-top: 1px solid var(--gs-border); }
.gs-add-video__preview h3 { margin: 0 0 12px; font-size: 14px; font-weight: 600; }
.gs-add-video__appearance-hint { color: var(--gs-muted-foreground); font-size: 12px; line-height: 1.5; margin: 12px 0 0; }
.gs-add-video {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gs-add-video__segment {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gs-add-video__segment button {
  min-height: 34px;
  padding: 6px 14px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gs-add-video__segment button.is-active {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

.gs-add-video__segment button:disabled {
  cursor: not-allowed;
  opacity: .5;
}

.gs-add-video__limit {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
  padding: 24px 28px;
}

.gs-add-video__limit p {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gs-add-video__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gs-add-video__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.gs-add-video__input {
  display: block;
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font-size: 14px;
  line-height: 1.4;
}

textarea.gs-add-video__input {
  min-height: 88px;
  resize: vertical;
}

.gs-add-video__input:focus,
.gs-add-video__input:focus-within {
  border-color: var(--gs-primary);
  outline: none;
  box-shadow: var(--gs-primary-shadow);
}

.gs-add-video__input--error {
  border-color: var(--gs-danger);
}

.gs-add-video__toggle-wrap {
  display: inline-flex;
}

.gs-add-video__toggle-wrap.is-disabled {
  pointer-events: none;
  opacity: .6;
}

.gs-add-video__thumb {
  width: 144px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
}

.gs-add-video__thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gs-add-video__new-category {
  align-self: flex-start;
  padding: 0;
  margin-top: 8px;
  color: var(--gs-primary);
  background: none;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gs-switch-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-switch-row__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-switch-row__label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-switch-row__help {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-switch-row__hint {
  display: block;
  margin-top: 4px;
  color: var(--gs-danger);
  font-size: 12px;
  line-height: 1.4;
}

.gs-add-video__why-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-add-video__why-list strong {
  color: var(--gs-foreground);
}

/* The kit's drawer body carries a 32px bottom padding, and a sticky element is
   clamped to its containing block — the body's *content* box — so the footer
   used to stop 32px short of the scroll area and the rows underneath it showed
   through that strip. The footer owns the bottom inset instead. */
.gs-drawer ::v-deep .gs-drawer__body {
  padding-bottom: 0;
}

.gs-add-video__footer {
  display: flex;
  position: sticky;
  bottom: 0;
  z-index: 2;
  gap: 10px;
  justify-content: flex-end;
  margin: 4px -28px 0;
  padding: 16px 28px 24px;
  background: var(--gs-surface);
  border-top: 1px solid var(--gs-border);
}

@media (max-width: 640px) {
  .gs-add-video__section summary { padding: 14px; gap: 8px; }
  .gs-add-video__section-content { padding: 0 14px 16px; }
  .gs-add-video__appearance ::v-deep .gs-radio-card { padding: 12px; }
  .gs-add-video__footer {
    margin: 4px -18px 0;
    padding: 14px 18px 18px;
  }
}
</style>
